import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Button, Space, Typography, Select, Switch, Divider } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';

const { Text } = Typography;
const { TextArea } = Input;

const fieldTypes = [
  { label: 'Text Input', value: 'text' },
  { label: 'Text Area', value: 'textarea' },
  { label: 'Rich Text', value: 'richtext' },
  { label: 'Number', value: 'number' },
  { label: 'Email', value: 'email' },
  { label: 'Phone', value: 'phone' },
  { label: 'URL', value: 'url' },
  { label: 'Date', value: 'date' },
  { label: 'Date & Time', value: 'datetime' },
  { label: 'Time', value: 'time' },
  { label: 'Dropdown', value: 'select' },
  { label: 'Multi-Select', value: 'multiselect' },
  { label: 'Radio Buttons', value: 'radio' },
  { label: 'Checkboxes', value: 'checkbox' },
  { label: 'File Upload', value: 'file' },
  { label: 'Currency', value: 'currency' },
  { label: 'Percentage', value: 'percentage' },
];

export default function RepeaterConfigPanel({ open, onClose, onSave, initialConfig }) {
  // Deep clone fields to avoid mutating readonly props
  const [fields, setFields] = useState(
    initialConfig?.fields ? initialConfig.fields.map(field => ({ ...field, options: field.options ? [...field.options] : [] })) : []
  );
  const [allowAdd, setAllowAdd] = useState(initialConfig?.allowAdd !== false);
  const [allowDelete, setAllowDelete] = useState(initialConfig?.allowDelete !== false);
  const [minItems, setMinItems] = useState(initialConfig?.minItems || 1);
  const [maxItems, setMaxItems] = useState(initialConfig?.maxItems || 50);
  const [itemLabel, setItemLabel] = useState(initialConfig?.itemLabel || 'Item');

  // Reset state when modal opens with new config
  useEffect(() => {
    if (open && initialConfig) {
      setFields(
        initialConfig.fields ? initialConfig.fields.map(field => ({ ...field, options: field.options ? [...field.options] : [] })) : []
      );
      setAllowAdd(initialConfig.allowAdd !== false);
      setAllowDelete(initialConfig.allowDelete !== false);
      setMinItems(initialConfig.minItems || 1);
      setMaxItems(initialConfig.maxItems || 50);
      setItemLabel(initialConfig.itemLabel || 'Item');
    }
  }, [open, initialConfig]);

  const handleAddField = () => {
    setFields([
      ...fields,
      {
        key: `field_${Date.now()}`,
        label: 'New Field',
        type: 'text',
        required: false,
        placeholder: '',
      },
    ]);
  };

  const handleRemoveField = (index) => {
    setFields(fields.filter((_, i) => i !== index));
  };

  const handleFieldChange = (index, key, value) => {
    const newFields = [...fields];
    newFields[index] = { ...newFields[index], [key]: value };
    setFields(newFields);
  };

  const handleSave = () => {
    const config = {
      fields,
      allowAdd,
      allowDelete,
      minItems,
      maxItems,
      itemLabel,
    };

    onSave(config);
    onClose();
  };

  return (
    <Modal
      title="Repeater Configuration"
      open={open}
      onCancel={onClose}
      onOk={handleSave}
      width={900}
      okText="Save"
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Text strong>Repeater Settings</Text>
          <Text type="secondary" style={{ display: 'block', fontSize: '13px', marginTop: '4px' }}>
            Configure what label to use for each repeated item
          </Text>
        </div>

        <div>
          <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
            Item Label (e.g., "Team Member", "Product", "Task")
          </Text>
          <Input
            placeholder="Item"
            value={itemLabel}
            onChange={(e) => setItemLabel(e.target.value)}
          />
        </div>

        <Divider />

        <div>
          <Text strong>Field Definitions</Text>
          <Text type="secondary" style={{ display: 'block', fontSize: '13px', marginTop: '4px' }}>
            Define the fields that appear in each repeated item. Leave empty for simple text area.
          </Text>
        </div>

        {fields.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 0', background: '#F9FAFB', borderRadius: '8px', border: '1px dashed #E5E7EB' }}>
            <Text type="secondary">No custom fields defined - will use simple text area</Text>
          </div>
        ) : (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {fields.map((field, index) => (
              <div
                key={index}
                style={{
                  padding: '16px',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  background: '#F9FAFB',
                }}
              >
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <Space style={{ width: '100%' }}>
                    <div style={{ flex: 1 }}>
                      <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                        Field Label
                      </Text>
                      <Input
                        placeholder="e.g., Full Name"
                        value={field.label}
                        onChange={(e) => handleFieldChange(index, 'label', e.target.value)}
                      />
                    </div>

                    <div style={{ width: '180px' }}>
                      <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                        Field Type
                      </Text>
                      <Select
                        style={{ width: '100%' }}
                        value={field.type}
                        onChange={(value) => handleFieldChange(index, 'type', value)}
                        options={fieldTypes}
                      />
                    </div>

                    <div style={{ marginTop: '18px' }}>
                      <Switch
                        checked={field.required}
                        onChange={(checked) => handleFieldChange(index, 'required', checked)}
                      />
                      <Text style={{ marginLeft: '8px', fontSize: '12px' }}>Required</Text>
                    </div>

                    <Button
                      icon={<DeleteOutlined />}
                      danger
                      onClick={() => handleRemoveField(index)}
                      style={{ marginTop: '18px' }}
                    />
                  </Space>

                  <div>
                    <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                      Placeholder Text (optional)
                    </Text>
                    <Input
                      placeholder="e.g., Enter full name"
                      value={field.placeholder}
                      onChange={(e) => handleFieldChange(index, 'placeholder', e.target.value)}
                    />
                  </div>

                  {['select', 'multiselect', 'radio', 'checkbox'].includes(field.type) && (
                    <div>
                      <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                        Options (one per line)
                      </Text>
                      <TextArea
                        rows={4}
                        placeholder={'Option 1\nOption 2\nOption 3'}
                        value={(field.options || []).join('\n')}
                        onChange={(e) => {
                          const options = e.target.value.split('\n');
                          handleFieldChange(index, 'options', options);
                        }}
                        onBlur={(e) => {
                          const cleanOptions = e.target.value.split('\n').filter(opt => opt.trim());
                          if (cleanOptions.length > 0) {
                            handleFieldChange(index, 'options', cleanOptions);
                          }
                        }}
                      />
                    </div>
                  )}
                </Space>
              </div>
            ))}
          </Space>
        )}

        <Button
          type="dashed"
          icon={<PlusOutlined />}
          onClick={handleAddField}
          block
        >
          Add Field
        </Button>

        <Divider />

        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div>
            <Text strong style={{ display: 'block', marginBottom: '8px' }}>Repeater Behavior</Text>
            <Space direction="vertical" size="small">
              <div>
                <Switch checked={allowAdd} onChange={setAllowAdd} />
                <Text style={{ marginLeft: '8px' }}>Allow users to add items</Text>
              </div>
              <div>
                <Switch checked={allowDelete} onChange={setAllowDelete} />
                <Text style={{ marginLeft: '8px' }}>Allow users to delete items</Text>
              </div>
            </Space>
          </div>

          <Space style={{ width: '100%' }}>
            <div style={{ flex: 1 }}>
              <Text strong style={{ display: 'block', marginBottom: '8px' }}>Minimum Items</Text>
              <Input
                type="number"
                min={0}
                max={100}
                value={minItems}
                onChange={(e) => setMinItems(parseInt(e.target.value) || 1)}
              />
            </div>

            <div style={{ flex: 1 }}>
              <Text strong style={{ display: 'block', marginBottom: '8px' }}>Maximum Items</Text>
              <Input
                type="number"
                min={1}
                max={100}
                value={maxItems}
                onChange={(e) => setMaxItems(parseInt(e.target.value) || 50)}
              />
            </div>
          </Space>
        </Space>

        <div style={{ background: '#F9F5FF', padding: '12px', borderRadius: '8px' }}>
          <Text strong style={{ fontSize: '13px' }}>
            Configuration Preview:
          </Text>
          <pre style={{ marginTop: '8px', fontSize: '12px', background: '#fff', padding: '8px', borderRadius: '4px', overflow: 'auto', maxHeight: '150px' }}>
            {JSON.stringify({ fields, allowAdd, allowDelete, minItems, maxItems, itemLabel }, null, 2)}
          </pre>
        </div>
      </Space>
    </Modal>
  );
}
